// useAstriaVietnamTab.ts
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../services/api";
import { useChat, Message, AstriaVietnamData } from "../../../contexts/ChatContext";
import { useCategories } from "../../../contexts/CategoryContext";
import { useSubCategories } from "../../../contexts/SubCategoryContext";
import { AstriaVietnamLanguage } from "./astriaVietnamLanguage";

// ─── Simple, linear flow for the Astria Vietnam wizard tabs — same
// architecture as useAstriaIndiaV2Tab.ts:
//
//   Fill form → Click Generate → API call → receive response → show it
//
// Deliberately does NOT go through ChatContext's chats/currentChat list —
// each wizard submission is a single, self-contained question-and-answer
// with no history and no other component needs to see it. "Continue in
// Chat" (goToChat) is the one place this hook hands off into the shared
// multi-turn /sanctuary chat world.
//
// Unlike India V2's hook, `send()` takes an optional second argument
// (`wizard`) — several Vietnam lanes need structured fields (date,
// purpose, space_type, direction, tarot_topic, dob, dob_time, dob_hour,
// gender) alongside the free-text `userMessage`, sent as `vietnamWizard`
// in the request body (see backend/controllers/chatController.js's
// isAstriaVietnam dispatch block, which reads vietnamWizard.dob/dob_time
// in preference to the stored profile DOB).

export interface VietnamWizardPayload {
  dob?: string;
  dob_time?: string;
  dob_hour?: string;
  gender?: string;
  date?: string;
  purpose?: string;
  space_type?: string;
  direction?: string;
  tarot_topic?: string;
}

interface UseAstriaVietnamTabOptions {
  subCategoryName: string;
  language: AstriaVietnamLanguage;
}

interface UseAstriaVietnamTabReturn {
  loading: boolean;
  error: string | null;
  message: Message | null;
  send: (text: string, wizard?: VietnamWizardPayload) => Promise<void>;
  reset: () => void;
  goToChat: () => Promise<void>;
}

const CATEGORY_NAME = "Astria Vietnam";

export function useAstriaVietnamTab({
  subCategoryName,
  language,
}: UseAstriaVietnamTabOptions): UseAstriaVietnamTabReturn {
  const navigate = useNavigate();
  const { startNewChat, setCurrentChat } = useChat();
  const { categories } = useCategories();
  const { subCategories } = useSubCategories();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<Message | null>(null);

  const resolveIds = (): { categoryId: string | null; subCategoryId: string | null } => {
    const category = categories.find((c) => c.name === CATEGORY_NAME);
    const categoryId = category?.id || null;
    const subCategory = categoryId
      ? subCategories.find(
          (sc) => sc.categoryId === categoryId && sc.name === subCategoryName,
        )
      : undefined;
    return { categoryId, subCategoryId: subCategory?.id || null };
  };

  const send = async (text: string, wizard?: VietnamWizardPayload) => {
    setLoading(true);
    setError(null);
    setMessage(null);

    const { categoryId, subCategoryId } = resolveIds();

    if (!categoryId) {
      setError(
        `The "${CATEGORY_NAME}" category isn't set up yet. Please try again shortly.`,
      );
      setLoading(false);
      return;
    }

    if (!subCategoryId) {
      setError(
        `The "${subCategoryName}" subcategory isn't set up yet. Please try again shortly.`,
      );
      setLoading(false);
      return;
    }

    try {
      const userId = localStorage.getItem("userId");
      const token = localStorage.getItem("token");
      const baseUrl = api.defaults.baseURL || "";

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(
        `${baseUrl}/backend/chat/create?stream=true`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            userMessage: text,
            categoryId,
            subCategoryId,
            userId: userId || undefined,
            language,
            vietnamWizard: wizard || undefined,
          }),
        },
      );

      if (!response.ok || !response.body) {
        let errMsg = "The server couldn't be reached. Please try again.";
        try {
          const errData = await response.json();
          if (errData?.message) errMsg = errData.message;
        } catch {
          // ignore — keep default message
        }
        setError(errMsg);
        setLoading(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let content = "";
      let astriaVietnamData: AstriaVietnamData | null = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";

        for (const part of parts) {
          for (const line of part.split("\n")) {
            if (!line.startsWith("data:")) continue;
            const dataStr = line.replace(/^data:\s*/, "");
            if (!dataStr) continue;

            let payload: any;
            try {
              payload = JSON.parse(dataStr);
            } catch {
              continue;
            }

            if (payload?.text) {
              content += payload.text;
            }
            if (payload?.done && payload?.astriaVietnamData) {
              astriaVietnamData = payload.astriaVietnamData;
            }
          }
        }
      }

      if (!content.trim()) {
        setError("We didn't receive a reading back. Please try again.");
        setLoading(false);
        return;
      }

      setMessage({
        id: `ai_${Date.now()}`,
        content,
        sender: "ai",
        timestamp: new Date(),
        categoryId,
        subCategoryId,
        astriaVietnamData: astriaVietnamData || undefined,
      });
    } catch (err) {
      console.error(`Astria Vietnam (${subCategoryName}) error:`, err);
      setError("The server couldn't be reached. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setMessage(null);
    setError(null);
  };

  // "Continue in Chat" opens the app-wide "Companion Talk" category itself
  // (no subcategory) — same destination as Sanctuary's floating Astria Talk
  // Talk button and every other Astria lane — not a fresh chat on the
  // Vietnam subcategory the user just used.
  const goToChat = async () => {
    try {
      const category = categories.find((c) => c.name === "Companion Talk");
      const newChat = await startNewChat(category?.id || undefined);
      setCurrentChat(newChat);
      navigate(`/sanctuary?chat=${newChat.id}`);
    } catch (err) {
      console.error("Failed to start Companion Talk chat:", err);
      setError("Failed to start new chat. Please try again.");
    }
  };

  return { loading, error, message, send, reset, goToChat };
}
